import type { RuleStep } from '@edgesub/converter';
import { Download, ExternalLink, FileArchive, Gauge, Pencil, Plus, Route, Trash2, Upload } from 'lucide-react';
import { motion } from 'motion/react';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import { decryptBackup, encryptBackup } from '../backup';
import { RuleEditor } from '../components/RuleEditor';
import { Button, EmptyState, Field, IconButton, Modal, Spinner, useToast } from '../components/ui';
import { errorMessage, formatDate } from '../lib';
import type { BackupDocument, RuleSet, UsageEstimate } from '../types';

export function SettingsPage({ sourceCodeUrl }: { sourceCodeUrl: string }) {
  const [ruleSets, setRuleSets] = useState<RuleSet[] | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [ruleName, setRuleName] = useState('');
  const [steps, setSteps] = useState<RuleStep[]>([]);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [ruleBusy, setRuleBusy] = useState(false);
  const [exportPassword, setExportPassword] = useState('');
  const [importPassword, setImportPassword] = useState('');
  const [importFileName, setImportFileName] = useState('');
  const [backupBusy, setBackupBusy] = useState(false);
  const [usage, setUsage] = useState<UsageEstimate | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { notify } = useToast();

  const load = useCallback(async () => {
    const [nextRuleSets, nextUsage] = await Promise.all([
      api.get<RuleSet[]>('/api/rule-sets'),
      api.get<UsageEstimate>('/api/usage-estimate'),
    ]);
    setRuleSets(nextRuleSets);
    setUsage(nextUsage);
  }, []);
  useEffect(() => { load().catch((error) => notify(errorMessage(error), 'error')); }, [load, notify]);

  const initialRule = useRef('');
  const ruleSnapshot = (name: string, ruleSteps: RuleStep[]) => JSON.stringify({ name, steps: ruleSteps });
  const createRule = () => { setEditingId(null); setRuleName(''); setSteps([]); initialRule.current = ruleSnapshot('', []); setRuleOpen(true); };
  const editRule = (ruleSet: RuleSet) => { setEditingId(ruleSet.id); setRuleName(ruleSet.name); setSteps(ruleSet.steps); initialRule.current = ruleSnapshot(ruleSet.name, ruleSet.steps); setRuleOpen(true); };
  const saveRule = async (event: FormEvent) => {
    event.preventDefault(); setRuleBusy(true);
    try {
      if (editingId) await api.put(`/api/rule-sets/${editingId}`, { name: ruleName, steps }); else await api.post('/api/rule-sets', { name: ruleName, steps });
      notify(editingId ? '规则链已更新' : '规则链已创建'); setRuleOpen(false); await load();
    } catch (error) { notify(errorMessage(error), 'error'); } finally { setRuleBusy(false); }
  };
  const removeRule = async (ruleSet: RuleSet) => {
    if (!window.confirm(`删除规则链“${ruleSet.name}”？引用它的发布项将改为不使用规则。`)) return;
    try { await api.delete(`/api/rule-sets/${ruleSet.id}`); notify('规则链已删除'); await load(); } catch (error) { notify(errorMessage(error), 'error'); }
  };

  const exportData = async () => {
    if (exportPassword.length < 8) return notify('备份密码至少需要 8 个字符', 'error');
    setBackupBusy(true);
    try {
      const backupDocument = await api.get<BackupDocument>('/api/backup/export');
      const blob = await encryptBackup(backupDocument, exportPassword);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = `edgesub-backup-${new Date().toISOString().slice(0, 10)}.json`; anchor.click();
      URL.revokeObjectURL(url); setExportPassword(''); notify('加密备份已导出');
    } catch (error) { notify(errorMessage(error), 'error'); } finally { setBackupBusy(false); }
  };
  const importData = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return notify('请选择备份文件', 'error');
    if (!importPassword) return notify('请输入备份密码', 'error');
    if (!window.confirm('导入会替换当前全部配置并重新生成发布令牌，继续吗？')) return;
    setBackupBusy(true);
    try {
      const document = await decryptBackup(file, importPassword);
      await api.post('/api/backup/import', document);
      setImportPassword(''); setImportFileName(''); if (fileRef.current) fileRef.current.value = '';
      notify('备份已导入，所有发布地址已重新生成'); await load();
    } catch (error) { notify(errorMessage(error), 'error'); } finally { setBackupBusy(false); }
  };

  return <>
    <header className="page-header"><div><span className="eyebrow">配置与数据</span><h1>设置</h1><p>维护规则链、加密备份和项目来源信息。</p></div></header>
    <section className="section-block">
      <div className="section-heading"><div><h2>规则链</h2><p>规则严格按顺序执行，每条正则在保存和运行前都会校验。</p></div><Button variant="secondary" icon={<Plus size={16} />} onClick={createRule}>新建规则链</Button></div>
      {!ruleSets ? <Spinner /> : ruleSets.length === 0 ? <EmptyState icon={<Route size={22} />} title="还没有规则链，发布时将保留原始节点" /> : <div className="settings-list">{ruleSets.map((ruleSet, index) => <motion.div key={ruleSet.id} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: Math.min(index * 0.04, 0.2), duration: 0.22 }}><div><strong>{ruleSet.name}</strong><span>{ruleSet.steps.length} 条规则 · 更新于 {formatDate(ruleSet.updatedAt)}</span></div><div className="item-actions"><IconButton label="编辑" onClick={() => editRule(ruleSet)}><Pencil size={16} /></IconButton><IconButton label="删除" onClick={() => void removeRule(ruleSet)}><Trash2 size={16} /></IconButton></div></motion.div>)}</div>}
    </section>

    {usage && <section className="section-block usage-section">
      <div className="section-heading"><div><h2>KV 写入估算</h2><p>根据当前刷新周期计算，不是 Cloudflare 的实时用量。</p></div><Gauge size={20} /></div>
      <div className={`usage-meter ${usage.scheduledWrites >= usage.stopAt ? 'danger' : usage.scheduledWrites >= usage.warningAt ? 'warning' : ''}`}>
        <div style={{ width: `${Math.min(100, usage.scheduledWrites / usage.freeLimit * 100)}%` }} />
      </div>
      <strong className="usage-value">预计 {usage.scheduledWrites.toLocaleString()} / {usage.freeLimit.toLocaleString()} 次 KV 写入/天</strong>
    </section>}

    <section className="section-block backup-section">
      <div className="section-heading"><div><h2>加密备份</h2><p>密码只在浏览器内用于 PBKDF2 与 AES-GCM，服务端不会收到或保存它。</p></div><FileArchive size={20} /></div>
      <motion.div className="backup-grid" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08, duration: 0.26 }}>
        <div><h3>导出配置</h3><p>包含配置与源数据，不包含管理密码、APP_SECRET 和会话。</p><Field label="备份密码"><input type="password" value={exportPassword} onChange={(event) => setExportPassword(event.target.value)} placeholder="至少 8 个字符" autoComplete="new-password" /></Field><Button variant="secondary" icon={<Download size={16} />} disabled={backupBusy} onClick={() => void exportData()}>导出加密文件</Button></div>
        <div><h3>恢复备份</h3><p>当前配置会被替换；为保证安全，所有公开地址都会轮换。</p><Field label="备份文件"><span className="file-control"><input ref={fileRef} type="file" accept="application/json,.json" onChange={(event) => setImportFileName(event.target.files?.[0]?.name ?? '')} /><span className="file-control-button">选择文件</span><span className="file-control-name">{importFileName || '未选择文件'}</span></span></Field><Field label="备份密码"><input type="password" value={importPassword} onChange={(event) => setImportPassword(event.target.value)} placeholder="导出时设置的密码" autoComplete="off" /></Field><Button variant="secondary" icon={<Upload size={16} />} disabled={backupBusy} onClick={() => void importData()}>解密并导入</Button></div>
      </motion.div>
    </section>

    <section className="section-block about-section"><div><span className="brand-mark small" aria-hidden="true">订</span><div><h2>EdgeSub</h2><p>AGPL-3.0-only · 转换核心固定参考 Sub-Store 2.36.22</p></div></div>{sourceCodeUrl && <a className="button button-secondary" href={sourceCodeUrl} target="_blank" rel="noreferrer"><ExternalLink size={16} />查看源代码</a>}</section>

    <Modal open={ruleOpen} title={editingId ? '编辑规则链' : '新建规则链'} onClose={() => setRuleOpen(false)} wide isDirty={() => ruleSnapshot(ruleName, steps) !== initialRule.current}>
      <form className="form-stack" onSubmit={saveRule}><Field label="名称"><input value={ruleName} onChange={(event) => setRuleName(event.target.value)} required maxLength={80} /></Field><RuleEditor steps={steps} onChange={setSteps} /><div className="form-actions"><Button type="button" variant="secondary" onClick={() => setRuleOpen(false)}>取消</Button><Button type="submit" disabled={ruleBusy}>{ruleBusy ? '保存中...' : '保存规则链'}</Button></div></form>
    </Modal>
  </>;
}
